import { SessionsSidebar } from "../../features/sessions/SessionsSidebar";
import { ChatPane } from "../../features/chat/ChatPane";
import { ArtifactsPanel } from "../../features/artifacts/ArtifactsPanel";
import { SettingsDialog } from "../../features/auth/SettingsDialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui/tabs";
import { Sheet, SheetContent, SheetTrigger } from "../../components/ui/sheet";
import { useState } from "react";
import { MessageSquare, FileText, Menu, PanelLeftClose, PanelLeftOpen } from "lucide-react";
import { Button } from "../../components/ui/button";

export function Shell() {
  const [activeViewTab, setActiveViewTab] = useState<string>("chat");
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarMinimized, setSidebarMinimized] = useState(false);

  return (
    <div className="flex h-screen w-screen overflow-hidden bg-background" style={{ height: '100dvh' }}>
      {/* Desktop Sidebar - Hidden on mobile, only shows on md and above */}
      {!sidebarMinimized && (
        <aside className="hidden md:flex flex-shrink-0 w-80 border-r border-border bg-card">
          <div className="w-full h-full">
            <SessionsSidebar />
          </div>
        </aside>
      )}
      
      {/* Main Content - Full width on mobile */}
      <main className="flex-1 flex flex-col min-w-0 w-full md:w-auto overflow-hidden relative">
        {/* Header - Full width with hamburger menu, sticky on mobile */}
        <header className="sticky top-0 z-50 h-16 w-full border-b border-border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 shadow-[0_1px_2px_0_rgba(0,0,0,0.05)] flex-shrink-0">
          <div className="flex h-full items-center justify-between px-3 sm:px-4 md:px-6">
            <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
              {/* Mobile hamburger menu */}
              <Sheet open={sidebarOpen} onOpenChange={setSidebarOpen}>
                <SheetTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="md:hidden shrink-0"
                    aria-label="Open menu"
                  >
                    <Menu className="h-5 w-5" />
                  </Button>
                </SheetTrigger>
                <SheetContent side="left" className="w-[85vw] max-w-sm p-0">
                  <SessionsSidebar onSessionSelect={() => setSidebarOpen(false)} />
                </SheetContent>
              </Sheet>

              {/* Desktop sidebar toggle button */}
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setSidebarMinimized(!sidebarMinimized)}
                className="hidden md:flex shrink-0"
                aria-label={sidebarMinimized ? "Expand sidebar" : "Collapse sidebar"}
              >
                {sidebarMinimized ? (
                  <PanelLeftOpen className="h-5 w-5" />
                ) : (
                  <PanelLeftClose className="h-5 w-5" />
                )}
              </Button>

              <div className="h-8 w-8 rounded-lg bg-gradient-to-br from-primary to-primary/60 flex items-center justify-center shrink-0">
                <span className="text-primary-foreground font-bold text-sm">P</span>
              </div>
              <h1 className="text-lg sm:text-xl font-semibold tracking-tight text-foreground truncate">Plumcot</h1>
            </div>
            <div className="shrink-0">
              <SettingsDialog />
            </div>
          </div>
        </header>
        
        {/* Main Content Area with Chat/Artifacts Tabs */}
        <div className="flex-1 flex flex-col overflow-hidden">
          <Tabs 
            value={activeViewTab} 
            onValueChange={setActiveViewTab} 
            className="flex-1 flex flex-col overflow-hidden"
          >
            <div className="border-b border-border px-4 md:px-6">
              <TabsList className="bg-transparent h-10 p-1 w-full md:w-auto">
                <TabsTrigger 
                  value="chat" 
                  className="data-[state=active]:bg-background data-[state=active]:shadow-sm px-4 text-sm font-medium flex items-center gap-2 flex-1 md:flex-initial"
                >
                  <MessageSquare className="h-4 w-4" />
                  <span className="hidden sm:inline">Chat</span>
                </TabsTrigger>
                <TabsTrigger 
                  value="artifacts"
                  className="data-[state=active]:bg-background data-[state=active]:shadow-sm px-4 text-sm font-medium flex items-center gap-2 flex-1 md:flex-initial"
                >
                  <FileText className="h-4 w-4" />
                  <span className="hidden sm:inline">Artifacts</span>
                </TabsTrigger>
              </TabsList>
            </div>
            <TabsContent value="chat" className="flex-1 overflow-hidden m-0 p-0">
              <ChatPane />
            </TabsContent>
            <TabsContent value="artifacts" className="flex-1 overflow-auto m-0 p-4 md:p-6">
              <ArtifactsPanel />
            </TabsContent>
          </Tabs>
        </div>
      </main>
    </div>
  );
}

